import React, { Fragment, useEffect, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import {
  ChatMessageWithIndex,
  ChatStore,
  isArticle,
  isExercise,
  isImage,
  MessageContent,
} from './chatStore.ts'
import { useSnapshot } from 'valtio/react'
import { ChatAudioRecordStore } from './chatAudioRecordStore.ts'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import { classed } from '@tw-classed/react'
import {
  cn,
  emptyFn,
  formatTimer,
  getLast,
  removeTextTag,
  splitBy,
  useMount,
} from '../../shared/lib/utils.ts'
import { DotsAnimation, Spinner } from '../../shared/ui/spinner.tsx'
import { useRive, useStateMachineInput } from '@rive-app/react-canvas'
import { AnimatePresence, motion } from 'framer-motion'
import { match, P } from 'ts-pattern'
import {
  createStoreContext,
  useIsMobile,
  useLazyRef,
  useStoreContext,
} from '../../shared/lib/hooks.ts'
import { ClickableWords } from '../../shared/ui/ClickableWords/ClickableWords.tsx'
import { Badge } from '../../shared/ui/card/card.tsx'
import { MatchWords } from '../exercises/matchWords/matchWords.tsx'
import { AiComment, Vocabulary } from '../../shared/api/chatApi.ts'
import { Button } from '../../shared/ui/button/button.tsx'
import { ErrorBoundary } from '@sentry/react'
import {
  Drawer,
  DrawerBar,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from '../../shared/ui/drawer/drawer.tsx'
import { isWebDomain } from '../../shared/urls.ts'
import { inputClassBase } from '../../shared/ui/textInput/textInput.tsx'
import { proxy } from 'valtio'
import { WithTooltip } from '../../shared/ui/tooltip/tooltip.tsx'
import { ImageHideBeforeLoad } from '../../shared/ui/images/images.tsx'
import { WriteTheGapss } from '../exercises/writeTheGaps.tsx'
import { VocabularyContainer } from './demoChat.tsx'
import { FormattedMessage, useIntl } from 'react-intl'
import { convertToId } from '../../shared/lib/stringUtils.ts'
import { Soundwave } from '../../shared/ui/soundwave/soundwave.tsx'
import { VoiceAuraCanvas } from '../../shared/ui/voiceAura/VoiceAuraCanvas.tsx'
import { useAppModule } from '../appContext.ts'
import { BuyTrial } from '../programs/buyTrial.tsx'
import { TrueOrFalseExercise } from '../exercises/trueOrFalse/TrueOrFalseExercise.tsx'
import { FillTheGaps } from '../exercises/fillTheGaps/fillTheGaps.tsx'
import { AnswerStatusDrawer } from '../exercises/answerStatusDrawer.tsx'
import { ListeningExercise } from '../exercises/listeningExercise/listeningExercise.tsx'
import { LessonIsCompleted } from '../lessonCompleted/lessonCompleted.tsx'
import { LessonStatistics } from '../lessonStatistics/lessonStatistics.tsx'
import { AppHeaderNavigationButton } from '../appHeader/appHeader.tsx'
import { TransparentGradient } from '../../shared/ui/gradient/TransparentGradient.tsx'
import { v4 as uuidv4 } from 'uuid'
import { Dialog } from '../../shared/ui/dialog/dialog.tsx'
// import { ChatJoyride } from '../joyride/ChatJoyride.tsx'

export function StageProgressBlockMobile() {
  const store = useStores().chatStore
  const chatState = useSnapshot(store.state)
  const isLessonCompleted = chatState.lessonCompleted
  const isMobile = useIsMobile()
  const isReadonly = chatState.viewMode === 'readonly'
  const isDemo = chatState.isDemo
  const getIsNavButton = () => {
    if (isReadonly) return true
    return !isDemo && !isLessonCompleted && isMobile
  }
  return (
    <div className="fixed left-0 top-0 z-20 flex h-100 w-full items-center bg-demo-mobile-header px-16 backdrop-blur-4 xl:static xl:bg-white xl:backdrop-blur-0">
      <div className="flex items-center">
        {getIsNavButton() && <AppHeaderNavigationButton />}
      </div>
      <div className="ml-16 flex-1 text-start text-18 font-bold">
        {chatState.isDemo ? (
          <FormattedMessage id="Demo lesson" />
        ) : (
          !!chatState.program && chatState.program.lesson_subject
        )}
      </div>
      {chatState.lessonType === 'lesson' && (
        <div className={cn('xl:hidden')}>
          <VocabularyDrawer />
        </div>
      )}
      {/* <HeaderHintView /> */}
    </div>
  )
}

export function messageStageElementId(stage: string | undefined) {
  return stage ? `__message-stage-${convertToId(stage)}` : ''
}

export const ChatStoreContext = createStoreContext<{
  chatStore: ChatStore
  recordStore: ChatAudioRecordStore
}>()

export function useStores() {
  return useStoreContext(ChatStoreContext)
}

const MessageText = classed.div(
  'relative flex flex-col gap-12 overflow-hidden whitespace-pre-wrap px-16 py-12 text-14 font-normal text-black',
  {
    variants: {
      is_ai: {
        false: 'ml-6 rounded-l-18 rounded-tr-18 bg-user-message',
        true: 'mr-8 w-full rounded-r-18 rounded-tl-18 bg-edman-message',
      },
    },
  },
)

interface MessageViewProps {
  message: ChatMessageWithIndex
  loading: boolean
  showText: boolean
  history?: boolean
  className?: string
}

const MessageButton = classed.button(
  'flex size-28 items-center justify-center rounded-full bg-edman-message text-message-button',
)

function MessageButtons(props: { message: ChatMessageWithIndex }) {
  const store = useStores().chatStore
  const intl = useIntl()
  const isRu = useSnapshot(useAppModule().appStore.state).uiLocale === 'ru'
  const translationLoading =
    props.message.showTranslation && !props.message.translation

  return (
    <div className="flex shrink-0 items-center gap-8">
      {props.message.statePlayBtn === 'pause' ? (
        <PauseButton messageId={props.message.id} />
      ) : props.message.statePlayBtn === 'load' ? (
        <Spinner className="mx-4 flex size-20" />
      ) : (
        <PlayButton hint={false} messageId={props.message.id} />
      )}
      {!isRu && (
        <WithTooltip tooltip={intl.formatMessage({ id: 'Translate' })}>
          {translationLoading ? (
            <Spinner className="mx-4 flex size-20" />
          ) : (
            <MessageButton
              type="button"
              onClick={() => {
                if (!store.state.loading) {
                  void store.toggleMessageTranslate(props.message.id)
                }
              }}
            >
              <Icon
                size="little"
                iconName="translate"
                className={cn(
                  'cursor-pointer transition-colors',
                  props.message.showTranslation && 'text-main',
                )}
              />
            </MessageButton>
          )}
        </WithTooltip>
      )}
    </div>
  )
}

function PlayButton(props: { hint: boolean; messageId: number }) {
  const store = useStores().chatStore
  const intl = useIntl()
  return (
    <WithTooltip tooltip={intl.formatMessage({ id: 'Play again' })}>
      <MessageButton
        className="pl-2"
        type="button"
        onClick={() => {
          if (!store.state.loading && !store.state.mute) {
            store.stopEdmanAudio()
            void store.play(props.messageId, true, props.hint)
            store.trackPlayMetrika()
          }
        }}
      >
        <Icon size="xs" iconName="playMessage" />
      </MessageButton>
    </WithTooltip>
  )
}

function PauseButton(props: { messageId: number }) {
  const store = useStores().chatStore
  const intl = useIntl()
  return (
    <WithTooltip tooltip={intl.formatMessage({ id: 'Pause play' })}>
      <MessageButton
        className="pl-2"
        type="button"
        onClick={() => {
          store.stopEdmanAudio()
          store.setStatePlayBtnMessage(props.messageId, 'play')
        }}
      >
        <Icon size="xs" iconName="pauseMessage" />
      </MessageButton>
    </WithTooltip>
  )
}

function PlayProgress(props: { messageId: number }) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const value =
    props.messageId != state.playingMessage?.messageId
      ? 0
      : state.playingMessage.progress * 100
  return <ProgressBar value={value} />
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function MessageView(props: MessageViewProps) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const showText = !props.message.is_ai || props.showText
  const selectedWords = state.selectedWords
  const messageId = props.message.id

  const messageText = props.message.text
  const translation =
    props.message.translation && removeTextTag(props.message.translation)
  return (
    <div
      id={messageStageElementId(props.message.stage)}
      className={cn(
        'flex',
        props.message.is_ai
          ? 'items-end'
          : state.isOnboardingLesson
          ? 'ml-[92px] justify-end'
          : 'justify-end',
        props.className,
      )}
    >
      {!props.message.is_ai && (
        <AiCommentButton
          id={props.message.id}
          showAiComment={props.message.showAiComment}
          comment={props.message.ai_comment}
        />
      )}
      <MessageText is_ai={props.message.is_ai}>
        {props.message.is_ai && (
          <img
            src="/images/aiLogo.png"
            className="hidden size-40 shrink-0 overflow-hidden rounded-full sm:inline"
            alt="AI Logo"
          />
        )}
        {props.loading ? (
          <>
            <div
              className={cn(
                'flex',
                !props.message.is_ai && 'w-full justify-end',
              )}
            >
              <DotsAnimation />
            </div>
            {props.message.is_ai && state.responseLoadingText && (
              <div className="mt-4">{state.responseLoadingText}</div>
            )}
          </>
        ) : (
          <>
            {showText ? (
              props.message.is_ai ? (
                <div className="flex-1">
                  <ClickableWords
                    key={messageText}
                    animateSpeed={
                      state.lastMessageId == props.message.id &&
                      state.viewMode !== 'readonly'
                        ? 'fast'
                        : undefined
                    }
                    disabled={false}
                    className="flex-1"
                    selectedWords={selectedWords}
                    onClick={(word, add) => {
                      store.handleOpenAddDrawer(word, add, messageId)
                    }}
                    contexts={state.hints?.contexts}
                  >
                    {messageText}
                  </ClickableWords>
                  <AnimatePresence>
                    {props.message.showTranslation && translation && (
                      <motion.div
                        className="overflow-hidden text-light"
                        initial={{ opacity: 0, height: '0' }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: '0' }}
                      >
                        <div className="pt-20">{translation}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="w-full text-right">{props.message.text}</div>
              )
            ) : (
              <PlayProgress messageId={props.message.id} />
            )}
          </>
        )}

        <AnimatePresence>
          {props.message.showAiComment && (
            <AiCommentBlock message={props.message} />
          )}
        </AnimatePresence>
      </MessageText>

      {props.message.is_ai && !props.history && (
        <MessageButtons message={props.message} />
      )}
    </div>
  )
}

function AiCommentButton({
  id,
  showAiComment,
  comment,
}: {
  id: number
  showAiComment: boolean | undefined
  comment: AiComment | null | undefined
}) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  if (comment === undefined) {
    return null
  }

  const hasComments = comment?.spell && comment.spell.length > 0
  return (
    <motion.button
      className={cn(
        'self-center rounded-full transition-colors',
        hasComments
          ? showAiComment
            ? 'bg-main'
            : 'bg-ai-button-inactive'
          : 'bg-none',
        hasComments ? 'cursor-pointer' : 'cursor-default',
      )}
      id={
        hasComments ? `ai-comment-button-${id}` : `no-ai-comment-button-${id}`
      }
      onClick={() => {
        if (hasComments) {
          store.toggleMessageAiComment(id)
          if (state.currentHint === 'chat_onboarding_snowflake_hint') {
            store.endTour()
          }
        }
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.1 }}
    >
      <Icon iconName={hasComments ? 'asterisk' : 'checkGreen'} size="xsm" />
    </motion.button>
  )
}

function AiCommentBlock(props: { message: ChatMessageWithIndex }) {
  const duration = 0.3
  return (
    <div className="-mx-16 -mb-12 divide-y bg-ai-comment-active text-black shadow-drop-inset-top">
      <motion.div
        className="mx-16"
        transition={{ opacity: { delay: duration, duration } }}
        initial={{ opacity: 0, height: '0', width: '0' }}
        animate={{ opacity: 1, height: 'auto', width: 'auto' }}
        exit={{ opacity: 0, height: '0', width: '0' }}
      >
        {props.message.ai_comment?.spell.map((x) => (
          <div className="flex flex-col gap-8 py-8" key={uuidv4()}>
            <div>{x.correct}</div>
            <div>{x.rule}</div>
          </div>
        ))}
      </motion.div>
    </div>
  )
}

const MessageListContainer = classed.div(
  'relative mt-auto flex w-full flex-col gap-12 pt-60',
)

function MessageList() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isReadonlyView = state.viewMode === 'readonly'
  const isReadingLesson =
    state.programTag?.toLowerCase().includes('reading') ?? false
  const isInitialReadingBatch = isReadingLesson && state.messages.length <= 3
  useEffect(() => {
    if (isInitialReadingBatch) return

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 300)
  }, [
    state.messages.length,
    state.loading,
    state.lastMessage.reply_hint,
    state.lastMessage.showHint,
    isInitialReadingBatch,
  ])
  useMount(() => {
    if (isInitialReadingBatch) return

    bottomRef.current?.scrollIntoView()
  })
  return (
    <div
      className={cn(
        'flex w-full flex-1 overflow-y-auto bg-white px-16 pt-40 [scrollbar-width:thin]',
        isReadonlyView && 'pb-24 pt-100',
      )}
    >
      <MessageListContainer>
        <DemoStart />
        {state.messages.map((x) => {
          const content = x.content && (
            <MessageContentView
              completed={state.lastMessageId != x.id}
              content={x.content}
              messageId={x.id}
            />
          )
          return (
            <Fragment key={x.id}>
              <MessageView
                showText={state.showMessageTexts}
                key={x.index}
                message={x}
                loading={false}
              />
              {content}
            </Fragment>
          )
        })}
        {state.loadingMessage ? (
          <MessageView
            showText={state.showMessageTexts}
            key="loading"
            message={state.loadingMessage}
            loading={true}
          />
        ) : (
          <MessageHint />
        )}
        <div ref={bottomRef}></div>
      </MessageListContainer>
    </div>
  )
}

function MessageHint() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const isRu = useSnapshot(useAppModule().appStore.state).uiLocale === 'ru'
  if (isRu) {
    return null
  }
  const lastMessage = getLast(state.messages)
  if (
    state.lessonCompleted ||
    isExercise(state.lastMessage.content) ||
    !lastMessage ||
    state.disableMessageHint
  ) {
    return null
  }
  const loading = lastMessage.showHint && !lastMessage.reply_hint
  const title = (
    <>
      {loading ? <Spinner /> : <Icon iconName="bulb" />}
      <FormattedMessage id="chat.hint" />
    </>
  )
  if (lastMessage.reply_hint && lastMessage.showHint) {
    return (
      <div className="relative ml-auto max-w-300 rounded-l-12 rounded-tr-12 bg-warning p-16 text-14 font-semibold">
        <div className="mb-8 flex items-center gap-4 text-16 font-bold text-warning-dark">
          {title}
        </div>
        <div>
          <ClickableWords
            disabled={false}
            selectedWords={state.selectedWords}
            onClick={(word, add) => {
              store.handleOpenAddDrawer(word, add, lastMessage.id)
            }}
          >
            {lastMessage.reply_hint.text}
          </ClickableWords>
        </div>
        <div className="mt-12 text-gray-dark">
          {lastMessage.reply_hint.translation}
        </div>

        <div className="absolute bottom-4 right-4 flex items-center gap-8">
          <PlayButton hint={true} messageId={lastMessage.id} />
        </div>
      </div>
    )
  }
  return (
    <Button
      onClick={() => {
        void store.showMessageHint()
      }}
      bg="custom"
      rounded="full"
      className="ml-auto w-fit gap-4 bg-white text-warning-dark shadow-button"
      size="little"
    >
      {title}
    </Button>
  )
}

const DemoStart = () => {
  const isMobile = useIsMobile()
  const state = useSnapshot(useStores().chatStore.state)
  if (!state.isDemo || !isMobile) {
    return null
  }
  return (
    <div className="my-48 flex flex-col items-center">
      <div className="my-20 text-center text-24 font-bold text-black">
        <FormattedMessage id="chat.demoStart" />
      </div>
    </div>
  )
}

export const ChatLessonOverlays = React.memo(() => {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  if (state.statistics.isStatPage) {
    return <LessonStatistics store={store} />
  }
  if (state.statistics.isLessonCompletedPage) {
    return <LessonIsCompleted store={store} />
  }
  return null
})

ChatLessonOverlays.displayName = 'ChatLessonOverlays'

const MessageContentContainer = classed.div('w-fit rounded-12 bg-white', {
  variants: {
    image: {
      true: 'overflow-hidden',
      false: 'p-16',
    },
  },
})

function MessageContentView(props: {
  content: MessageContent
  completed: boolean
  messageId: number
}) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const hasImage = isImage(props.content)
  const content = match(props.content)
    .with({ image: P._ }, (x) => (
      <ImageHideBeforeLoad className="max-h-[40vh]" src={x.image} />
    ))
    .with({ article: P._ }, (x) => (
      <Article
        title={x.article.title}
        body={x.article.body}
        messageId={props.messageId}
      />
    ))
    .with({ text: P._ }, (x) => {
      return (
        <div className="whitespace-pre-wrap">
          <ClickableWords
            animateSpeed="fast"
            disabled={false}
            selectedWords={state.selectedWords}
            onClick={(word, add) => {
              store.handleOpenAddDrawer(word, add, props.messageId)
            }}
            contexts={state.hints?.contexts}
          >
            {x.text}
          </ClickableWords>
        </div>
      )
    })
    .with({ matchWords: P._ }, (x) => (
      <MatchWords
        completed={props.completed}
        words={x.matchWords}
        onComplete={(x, y, payload, wrongAnswersCount, wrongAnswers) => {
          store.onExerciseComplete(
            x,
            y,
            payload,
            wrongAnswersCount,
            wrongAnswers,
          )
        }}
        viewMode={state.viewMode}
      />
    ))
    .with({ writeTheGaps: P._ }, (x) => (
      <WriteTheGapss
        completed={props.completed}
        texts={x.writeTheGaps}
        onComplete={(x) => {
          store.onExerciseComplete(x)
        }}
      />
    ))
    .with({ fillTheGaps: P._ }, (x) => (
      <FillTheGaps
        completed={props.completed}
        texts={x.fillTheGaps}
        onComplete={(x, y, payload) => {
          store.onExerciseComplete(x, y, payload)
        }}
        viewMode={state.viewMode}
      />
    ))
    .with({ trueOrFalse: P._ }, (x) => (
      <TrueOrFalseExercise
        onComplete={(x, y, payload) => {
          store.onExerciseComplete(x, y, payload)
        }}
        questions={x.trueOrFalse}
        viewMode={state.viewMode}
      />
    ))
    .with({ listening: P._ }, (x) => (
      <ListeningExercise
        onComplete={(x, y, payload) => {
          store.onExerciseComplete(x, y, payload)
        }}
        completed={props.completed}
        questions={x.listening}
        viewMode={state.viewMode}
      />
    ))
    .exhaustive()
  if (isExercise(props.content) || isArticle(props.content)) {
    return content
  }
  return (
    <ErrorBoundary
      fallback={
        <span className="text-alert">
          <FormattedMessage id="contentError" />
        </span>
      }
    >
      <MessageContentContainer image={hasImage}>
        {content}
      </MessageContentContainer>
    </ErrorBoundary>
  )
}

export const VocabularyHeader = React.memo((props: { className?: string }) => {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const [newWords, topicWords] = splitBy(
    state.vocabulary,
    (x) => x.is_user_added,
  )

  return (
    <div className={cn('mb-16 gap-24 text-14 font-extrabold', props.className)}>
      {!state.isDemo && (
        <div className="flex items-center gap-8">
          <Badge size="small" bg="orange">
            {topicWords.length}
          </Badge>
          <FormattedMessage id="Topic words" />
        </div>
      )}
      <div className="flex items-center gap-8">
        <Badge size="small" bg="purple-light">
          {newWords.length}
        </Badge>
        <FormattedMessage id="My words" />
      </div>
    </div>
  )
})

VocabularyHeader.displayName = 'VocabularyHeader'

interface VocabularyState {
  openedTopicWordIndex: number | null
  openedMyWordsElement: string | null
}

type VocabType = 'myWords' | 'topicWords'

const CircleDot = (props: { dot: boolean }) => {
  return (
    <div className="flex size-20 items-center justify-center rounded-full border-2 border-main">
      {props.dot && (
        <div className="size-12 rounded-full bg-main animate-in fade-in"></div>
      )}
    </div>
  )
}

const WordBlock = React.memo(
  (props: {
    vocabulary: Vocabulary
    state: VocabularyState
    opened: boolean
    index: number
    type: VocabType
  }) => {
    const x = props.vocabulary
    const opened = props.opened
    const store = useStores().chatStore
    const chatState = useSnapshot(store.state)
    useEffect(() => {
      if (
        chatState.currentHint === 'start_vocabulary_hint' &&
        chatState.isOnboardingLesson
      ) {
        setTimeout(() => {
          props.state.openedMyWordsElement = x.word_original
        }, 300)
      }
    }, [
      chatState.currentHint,
      chatState.isOnboardingLesson,
      props.state,
      x.word_original,
    ])
    const handleToggleWord = () => {
      if (props.type === 'myWords') {
        props.state.openedMyWordsElement =
          x.word_original === props.state.openedMyWordsElement
            ? null
            : x.word_original
      } else {
        props.state.openedTopicWordIndex =
          Number.isInteger(props.index) &&
          props.index === props.state.openedTopicWordIndex
            ? null
            : props.index
      }
    }

    return (
      <motion.div
        animate={{
          backgroundColor:
            x.word_original === chatState.lastAddVocabWord
              ? ['#F8F9FA', 'rgba(141, 171, 254, 0.5)', '#F8F9FA']
              : '#F8F9FA',
        }}
        exit={{ opacity: 0, height: 0 }}
        transition={{
          duration: 0.8,
          times: [0, 0.5, 1],
          ease: 'easeInOut',
        }}
        key={`${x.word_original}-word-block`}
        onClick={handleToggleWord}
        className={cn(
          'flex w-full cursor-pointer gap-8 overflow-hidden rounded-12 bg-gray-light p-16',
        )}
        id={`word-block-${x.word_original}`}
      >
        <div className="flex h-24 items-center">
          <CircleDot dot={opened} />
        </div>
        <WordBlockContent opened={opened} word={x} type={props.type} />
      </motion.div>
    )
  },
)
WordBlock.displayName = 'WordBlock'

export function VocabularyView(props: { isDesktop?: boolean }) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const intl = useIntl()
  const isRu = useSnapshot(useAppModule().appStore.state).uiLocale === 'ru'
  if (isRu) return null
  const lastAddWordVocab = state.lastAddVocabWord
  const [myWords, topicWords] = splitBy(
    state.vocabulary,
    (x) => x.is_user_added,
  )
  const myWordsReversed = myWords.reverse()
  const stateProxy = useLazyRef(() =>
    proxy<VocabularyState>({
      openedTopicWordIndex: null,
      openedMyWordsElement: lastAddWordVocab,
    }),
  ).current
  const localState = useSnapshot(stateProxy)

  const textId = state.isDemo
    ? 'vocabularyPlaceholderDemo'
    : 'vocabularyPlaceholder'
  const description = state.vocabulary.length == 0 && (
    <div>
      <FormattedMessage id={textId} />
    </div>
  )

  const blocks = (words: Vocabulary[], type: VocabType) => (
    <>
      {words.map((x, i) => {
        const opened =
          type === 'myWords'
            ? localState.openedMyWordsElement === x.word_original
            : localState.openedTopicWordIndex === i
        return (
          <WordBlock
            opened={opened}
            key={`${x.word_original}-${x.id}`}
            vocabulary={x}
            state={stateProxy}
            index={i}
            type={type}
          />
        )
      })}
    </>
  )

  const header = (text: string) => (
    <div className="pl-12 font-nunito-7-semicondensed text-16 font-bold text-light">
      {text}
    </div>
  )
  return (
    <VocabularyContainer>
      <div className="flex flex-col">
        {state.vocabulary.length == 0 ? (
          description
        ) : (
          <>
            <div className="mb-24 flex flex-col gap-8">
              {header(
                `${intl.formatMessage({ id: 'Topic words' })} (${
                  topicWords.length
                })`,
              )}
              {blocks(topicWords, 'topicWords')}
            </div>
            <div
              className="flex flex-col gap-8 will-change-auto"
              id={!props.isDesktop ? 'vocab-header-words' : ''}
            >
              {header(
                `${intl.formatMessage({ id: 'My words' })}  (${
                  myWords.length
                })`,
              )}
              <AnimatePresence mode="popLayout" initial={false}>
                {myWordsReversed.map((x, i) => (
                  <motion.div
                    key={x.word_original}
                    layoutId={`word-item-${x.word_original}`}
                    className="relative"
                    initial={{ opacity: 0, height: 0, y: -50 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30,
                      opacity: {
                        duration: 0.2,
                      },
                      exit: { duration: 0.1 },
                    }}
                  >
                    <WordBlock
                      opened={
                        localState.openedMyWordsElement === x.word_original
                      }
                      vocabulary={x}
                      state={stateProxy}
                      index={i}
                      type="myWords"
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </VocabularyContainer>
  )
}

export function VocabularyColumn() {
  const isRu = useSnapshot(useAppModule().appStore.state).uiLocale === 'ru'
  if (isRu) return null

  return (
    <div className="hidden size-full w-400 flex-col gap-16 pb-16 sm:flex">
      <div
        className={cn(
          'overflow-y-auto rounded-24 [scrollbar-width:thin] sm:shadow-card xl:max-h-full',
        )}
      >
        <VocabularyView isDesktop={true} />
      </div>
      {/* commented 20.03.2025 */}
      {/* <DesktopHint /> */}
      {isWebDomain && <BuyTrial vertical={true} />}
    </div>
  )
}

const TranslationBlock = ({
  translation,
}: {
  translation: string | undefined
}) => {
  if (!translation) return
  return (
    <div className="mt-12">
      <div>
        <div className="text-14 font-extrabold text-light">
          <FormattedMessage id="Translation:" />
        </div>
        <div className="text-16 font-bold">{translation}</div>
      </div>
    </div>
  )
}

function WordBlockContent(props: {
  opened: boolean
  word: Vocabulary
  type: VocabType
}) {
  const store = useStores().chatStore
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!props.opened) return
    const element = textRef.current
    if (element) {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
      setIsClamped(element.scrollHeight > lineHeight * 2)
    }
  }, [props.opened])

  const wordInfoBlock = () => {
    if (props.word.context) {
      return (
        <>
          <TranslationBlock translation={props.word.translation} />
          <div
            className={`mt-8 overflow-hidden leading-6 ${
              isExpanded ? 'line-clamp-none' : 'line-clamp-2'
            }`}
            ref={textRef}
          >
            <div>{props.word.context}</div>
          </div>
          {isClamped && (
            <button
              className="mt-2 font-nunito-7-semicondensed font-extrabold text-blue5"
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded((prev) => !prev)
              }}
            >
              <FormattedMessage id={isExpanded ? 'hide' : 'showMore'} />
            </button>
          )}
        </>
      )
    }
    if (props.word.explain) {
      return (
        <>
          <TranslationBlock translation={props.word.translation} />
          <div className="mt-8 overflow-hidden">{props.word.explain}</div>
        </>
      )
    }
    return <Spinner size="small" className="my-8" />
  }

  const handleDeleteWord = (e: React.MouseEvent) => {
    e.stopPropagation()
    void store.removeWord(props.word.id)
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-8 text-16 leading-6">
        <motion.span
          className="font-nunito-7-semicondensed text-16 font-bold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: 'easeInOut',
          }}
          key={props.word.word_normal}
        >
          {props.word.word_normal}
        </motion.span>
        {props.opened && props.word.explain && (
          <>
            <Icon
              size="sm"
              iconName="play"
              className={cn('cursor-pointer animate-in fade-in')}
              onClick={(e) => {
                e.stopPropagation()
                store.playWord(props.word.word_normal)
              }}
            />
            {props.type !== 'topicWords' && (
              <Icon
                size="sm"
                iconName="delete"
                className={cn(
                  'ml-auto cursor-pointer text-light animate-in fade-in',
                )}
                onClick={handleDeleteWord}
              />
            )}
          </>
        )}
      </div>
      <AnimatePresence>
        {props.opened && (
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: '0' }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: '0' }}
          >
            {wordInfoBlock()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const ChatViewContainer = classed.div(
  'relative flex max-h-[calc(100vh-36px)] w-full flex-col overflow-hidden sm:max-w-800 xl:rounded-t-24 xl:bg-white xl:shadow-container',
  {
    variants: {
      loading: {
        true: 'items-center justify-center',
      },
    },
  },
)

// commented 20.03.2025. Hide Desktop hint
// const DesktopHint = () => {
//   const store = useStores().chatStore
//   const { currentHint } = useSnapshot(store.state)

//   const handleCloseHint = () => {
//     store.setCurrentHint(null)
//   }
//   const isSnowflakeHint = currentHint === 'chat_onboarding_snowflake_hint'
//   const isVocabHint = currentHint === 'start_vocabulary_hint'
//   const shouldViewHint = isSnowflakeHint || isVocabHint

//   const preparedVideoUrlMp4 = isSnowflakeHint
//     ? 'https://img.edman.ai/videos/desktop_tooltip_snowflake.mp4'
//     : 'https://img.edman.ai/videos/desktop_tooltip_add_words.mp4'

//   const preparedVideoUrlWebm = isSnowflakeHint
//     ? 'https://img.edman.ai/videos/desktop_tooltip_snowflake.webm'
//     : 'https://img.edman.ai/videos/desktop_tooltip_add_words.webm'
//   return (
//     <AnimatePresence mode="wait">
//       {shouldViewHint && (
//         <motion.div
//           className="flex min-h-[360px] flex-col gap-12 rounded-24 bg-black p-12"
//           key={currentHint}
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           transition={{
//             duration: 0.2,
//             ease: 'easeInOut',
//           }}
//         >
//           <video autoPlay loop className="rounded-12">
//             <source
//               src={store.getVideoBlobUrl(preparedVideoUrlMp4)}
//               type="video/mp4; codecs='hvc1'"
//             />
//             <source
//               src={store.getVideoBlobUrl(preparedVideoUrlWebm)}
//               type="video/webm"
//             />
//           </video>
//           <div className="flex flex-col gap-4 text-white">
//             <p className="text-18 font-extrabold">
//               <FormattedMessage
//                 id={
//                   isSnowflakeHint
//                     ? 'desktopSnowflakeHintTitle'
//                     : 'desktopAddWordsHintTitle'
//                 }
//               />
//             </p>
//             <p className="text-14 font-medium">
//               <FormattedMessage
//                 id={
//                   isSnowflakeHint
//                     ? 'desktopSnowflakeHintText'
//                     : 'desktopAddWordsHintText'
//                 }
//               />
//             </p>
//           </div>
//           <Button
//             onClick={handleCloseHint}
//             size="little"
//             rounded="full"
//             bg="blue-gradient-shadow-inset"
//           >
//             <FormattedMessage id="ok" />
//           </Button>
//         </motion.div>
//       )}
//     </AnimatePresence>
//   )
// }

export function ChatView(props: { className?: string }) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const isLoading = state.loading == 'chat'

  // commented 20.03.2025. Hide Desktop hint
  //   useEffect(() => {
  //     if (state.isOnboardingLesson) {
  //       store.preloadVideos()

  //       return () => {
  //         store.clearVideoCache()
  //       }
  //     }
  //   }, [state.isOnboardingLesson, store])

  return (
    <ChatViewContainer className={props.className} loading={isLoading}>
      {isLoading ? (
        <Spinner size="large" />
      ) : (
        <>
          {state.showChatBlock && (
            <>
              <StageProgressBlockMobile />
              <MessageList />
              <Footer />
            </>
          )}
        </>
      )}
      <AnswerStatusDrawer />
      {/* <ChatJoyride /> */}
    </ChatViewContainer>
  )
}

export const AddWordDialog = () => {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  return (
    <Dialog.Root
      open={state.addWordDrawer.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          store.handleCloseAddDrawer()
        }
      }}
    >
      <Dialog.Content>
        <div className="flex h-auto flex-col">
          <div className="flex flex-col">
            <AddWordDialogElements />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export const AddWordDialogElements = () => {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const isButtonDisabled =
    state.wordWaitToAdd === state.addWordDrawer.word &&
    !state.addWordDrawer.shouldAdd
  return (
    <>
      <h3 className="text-22 font-bold">
        <FormattedMessage
          id={state.addWordDrawer.shouldAdd ? 'addWord' : 'removeWord'}
        />
      </h3>
      <p className="mt-14 text-14 xl:mt-12">
        <FormattedMessage
          id={
            state.addWordDrawer.shouldAdd
              ? 'addCurrentWord'
              : 'removeCurrentWord'
          }
          values={{ word: `"${state.addWordDrawer.word}"` }}
        />
      </p>
      <Button
        rounded="full"
        size="large"
        bg="blue-gradient"
        onClick={() => {
          void store.handleWord()
          store.handleCloseAddDrawer()
        }}
        className="mt-48 xl:mt-32"
        id="add-word-drawer-button"
        disabled={isButtonDisabled}
      >
        {isButtonDisabled ? (
          <Spinner size="small" />
        ) : (
          <FormattedMessage
            id={state.addWordDrawer.shouldAdd ? 'add' : 'chat.removeWord'}
          />
        )}
      </Button>

      <Button
        bg="gray-gradient"
        rounded="full"
        size="large"
        onClick={() => {
          store.handleCloseAddDrawer()
        }}
        className="mb-32 mt-12 xl:mb-0"
      >
        <FormattedMessage id="cancel" />
      </Button>
    </>
  )
}

export function AddWordDrawer() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)

  return (
    <Drawer
      open={state.addWordDrawer.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          store.handleCloseAddDrawer()
        }
      }}
    >
      <DrawerContent
        direction="bottom"
        className="mx-auto mt-24 flex h-auto max-w-800 flex-col rounded-t bg-white"
      >
        <DrawerBar />
        <div className="flex h-auto flex-col">
          <div className="flex flex-col  px-16 pt-16">
            <AddWordDialogElements />
            <DrawerClose asChild>
              <div className="absolute right-16 flex size-32 items-center justify-center rounded-full bg-gray">
                <Icon iconName="close" className="text-black" />
              </div>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

const HintWrapper = classed.div(
  'flex h-[calc(100%-100px)] w-full flex-col text-white',
)

export function ClickWordBlock() {
  return (
    <HintWrapper className="items-center justify-center px-16 text-center">
      <h3 className="text-24 font-extrabold">
        <FormattedMessage id="hints.clickWordTitle" />
      </h3>
      <p className="mt-12 text-16">
        <FormattedMessage
          id="hints.clickWordText"
          values={{
            click: (
              <span className="font-black">
                <FormattedMessage id="click" />
              </span>
            ),
          }}
        />
      </p>
      <img src="/images/hints/clickWordHand.svg" className="mt-32 size-fit" />
    </HintWrapper>
  )
}

export function AllWordsBlock() {
  return (
    <HintWrapper className="justify-start pl-[20%] pr-32">
      <div className="flex items-end justify-center gap-16 text-24 font-extrabold">
        <h3>
          <FormattedMessage id="hints.allWordsTitle" />
        </h3>
        <img src="/images/hints/topRightArrow.svg" className="mb-16 size-fit" />
      </div>
      <p className="mt-12 text-16">
        <FormattedMessage id="hints.allWordsText" />
      </p>
    </HintWrapper>
  )
}

const TextContainer = classed.div(
  'mt-12 flex gap-12 font-nunito-7-semicondensed text-16',
)

export function OrthographyHint() {
  return (
    <HintWrapper>
      <div className="mx-auto flex max-w-[500px] flex-col items-center px-12">
        <img
          className="w-full max-w-[500px] object-cover"
          src="/images/orthoHint.png"
          alt="orthographyHint"
        />
        <div className="mt-32 flex flex-col px-32">
          <h3 className="text-center text-24 font-extrabold text-white">
            <FormattedMessage id="orthoHintTitle" />
          </h3>
          <TextContainer className="mt-24">
            <Icon iconName="checkGreen" />
            <FormattedMessage id="orthoHintGreenText" />
          </TextContainer>
          <TextContainer>
            <div className="flex size-18 items-center justify-center rounded-full bg-ai-button-inactive">
              <Icon iconName="asterisk" size="xsm" />
            </div>
            <FormattedMessage id="orthoHintSnowText" />
          </TextContainer>
          <TextContainer>
            <Icon iconName="clickHand" size="large" className="h-[34px]" />
            <p>
              <FormattedMessage
                id="orthoHintClick"
                values={{
                  click: (
                    <span className="font-semibold">
                      <FormattedMessage id="clickCapitalize" />
                    </span>
                  ),
                }}
              />
            </p>
          </TextContainer>
        </div>
      </div>
    </HintWrapper>
  )
}

const states = [
  'idle',
  'recording',
  'cancel',
  'loading_start',
  'loading',
  'loading_to_idle',
  'disabled',
  'disabled_to_idle',
] as const

function RecordButtonIcon() {
  const store = useStores().chatStore
  const recordStore = useStores().recordStore
  const state = useSnapshot(store.state)
  const recordState = useSnapshot(recordStore.state)

  const animationState = state.chatInputUnavailable
    ? 'disabled'
    : state.sendButtonDisabled
    ? 'loading_start'
    : recordState.recording
    ? 'recording'
    : 'idle'

  const { rive, RiveComponent } = useRive({
    src: '/animations/microphone.riv',
    stateMachines: 'State Machine 1',
    animations: 'idle',
    autoplay: true,
  })
  const stateMachineInput = useStateMachineInput(
    rive,
    'State Machine 1',
    'Number 1',
  )
  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (stateMachineInput) {
      const getCurrentState = () => states[Number(stateMachineInput.value)]
      const nextState =
        getCurrentState() == 'recording' && animationState == 'idle'
          ? 'cancel'
          : getCurrentState() == 'disabled'
          ? 'disabled_to_idle'
          : animationState == 'idle' && getCurrentState() == 'loading'
          ? 'loading_to_idle'
          : animationState

      stateMachineInput.value = states.indexOf(nextState)
      function transfer(
        from: (typeof states)[number],
        to: (typeof states)[number],
        delay: number,
        cb = emptyFn,
      ) {
        if (getCurrentState() == from) {
          setTimeout(() => {
            if (stateMachineInput && getCurrentState() == from) {
              stateMachineInput.value = states.indexOf(to)
              cb()
            }
          }, delay)
        }
      }
      transfer('loading_start', 'loading', 1000)
      transfer('disabled_to_idle', 'loading_start', 500, () => {
        transfer('loading_start', 'loading', 1000)
      })
    }
  }, [rive, animationState, stateMachineInput])

  return <RiveComponent />
}

const ChatTextInputContainer = motion(
  classed.div('flex w-full items-center gap-12'),
)

export const ChatTextInput = () => {
  const store = useStores().chatStore
  const state = useSnapshot(store.state, { sync: true })
  const recoding = state.loading == 'record'
  const intl = useIntl()

  const recordStore = useStores().recordStore
  const recordState = useSnapshot(recordStore.state)

  const input = (
    <>
      <AnimatePresence initial={false}>
        {!state.showTextInput && <MicRecordBlock />}
      </AnimatePresence>

      <ChatControlButton
        onClick={() => {
          store.toggleMute()
        }}
        className="absolute inset-x-16 z-20"
      >
        <Icon iconName={state.mute ? 'mute' : 'unmute'} />
      </ChatControlButton>

      <ChatTextInputContainer
        className={cn(
          'ml-60 rounded-6 border-2 border-text-area-border shadow-inset-text-area',
          state.inputDisabled ? 'bg-gray' : 'bg-white',
        )}
      >
        <TextareaAutosize
          value={
            recoding ? formatTimer(recordState.recordTimer) : state.currentText
          }
          data-testid="chat-input"
          placeholder={intl.formatMessage({ id: 'Message' })}
          disabled={state.inputDisabled}
          autoFocus={state.showTextInput}
          autoComplete="off"
          readOnly={recoding}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void store.sendCurrentText()
            }
          }}
          onChange={(e) => {
            store.setCurrentText(e.currentTarget.value)
          }}
          maxRows={4}
          className={cn(
            'w-full resize-none rounded-6 bg-transparent px-16 py-8 text-20 leading-tight [scrollbar-width:thin] placeholder:pt-4 placeholder:text-14',
            inputClassBase,
            recoding && 'text-right',
          )}
        />
        <SendButton textInput={true} />
      </ChatTextInputContainer>
    </>
  )

  const completeLessonButton = (
    <Button
      size="extralarge"
      className="w-full"
      rounded="full"
      disabled={!state.lessonCompleted}
      bg="blue-gradient"
      onClick={() => {
        store.goToLessonCompletedPage()
        store.disposeEdmanAudio()
      }}
    >
      <FormattedMessage id="Complete the lesson" />
    </Button>
  )

  return (
    <div className="relative flex h-[148px] shrink-0 items-center bg-white px-16 sm:mx-0">
      <TransparentGradient direction="top" />
      {state.lessonCompleted ? (
        completeLessonButton
      ) : !state.isExercise ? (
        input
      ) : (
        <></>
      )}
    </div>
  )
}

function SendButton(props: { textInput: boolean }) {
  const store = useStores().chatStore
  const recordStore = useStores().recordStore
  const state = useSnapshot(store.state, { sync: true })

  const postfix = isImage(getLast(state.messages)?.content) ? 'demo_image' : ''

  const buttonSendId = state.isDemo && postfix ? 'send_' + postfix : undefined

  const sendButton = (
    <Button
      size={props.textInput ? 'medium' : 'large'}
      rounded={props.textInput ? undefined : 'full'}
      bg="blue-gradient-shadow-inset"
      className={cn(
        'mr-4 gap-12 whitespace-nowrap',
        !props.textInput && 'mr-0 w-fit min-w-44 px-0 pr-4',
      )}
      id={buttonSendId}
      disabled={state.sendButtonDisabled}
      onClick={() => {
        if (props.textInput) {
          void store.sendCurrentText()
        } else {
          void recordStore.stopRecording(true)
        }
      }}
    >
      {props.textInput && <FormattedMessage id="Send" />}
      <Icon
        iconName="send"
        className={cn(props.textInput ? '-mr-8' : '-ml-16')}
      />
    </Button>
  )
  const micButton = (
    <button
      id="record-button"
      className="mr-16 text-blue5 disabled:text-gray-dark"
      disabled={state.sendButtonDisabled}
      onClick={() => {
        store.setShowTextInput(false)
      }}
    >
      <Icon iconName="mic" />
    </button>
  )

  return state.currentText || !props.textInput ? sendButton : micButton
}

function Footer() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)

  return state.viewMode === 'readonly' ? null : <ChatTextInput />
}

const ChatControlButton = (props: {
  onClick: () => void
  className?: string
  children: React.ReactNode
}) => {
  return (
    <button
      onClick={props.onClick}
      className={`flex size-44 items-center justify-center rounded-full bg-purple-light2 text-purple5 opacity-60 shadow-control-button outline-none active:shadow-none ${props.className}`}
    >
      {props.children}
    </button>
  )
}

export function MictoTooltip() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  return (
    <AnimatePresence>
      {state.shouldViewMicroTooltip && !state.sendButtonDisabled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute -top-12 w-fit whitespace-nowrap text-14 font-bold text-gray5"
        >
          <FormattedMessage id="ClickToRecord" />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function MicRecordBlock() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const recording = state.loading == 'record'
  const recordStore = useStores().recordStore
  const streamService = useAppModule().streamService
  const showMic = !state.completed
  const micButtonRef = useRef<HTMLButtonElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)

  useEffect(() => {
    recordStore.reset()
  }, [showMic, state.showHistory, recordStore])

  useEffect(() => {
    if (!recording) {
      setStream(null)
      return
    }
    const sync = () => {
      const next = streamService.getStream()
      if (next) setStream(next)
    }
    sync()
    if (streamService.getStream()) return
    const id = window.setInterval(() => {
      sync()
      if (streamService.getStream()) window.clearInterval(id)
    }, 50)
    return () => {
      window.clearInterval(id)
    }
  }, [recording, streamService])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pointer-events-none absolute inset-x-0 -top-80 bottom-0 isolate z-30 overflow-visible"
    >
      <div className="absolute inset-x-0 bottom-0 h-[148px] bg-white sm:rounded-t" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 flex h-[148px] items-end justify-center px-24 pb-16">
        <div className="flex h-100 w-[80%] items-center justify-center">
          <Soundwave
            className="h-120 w-full"
            getData={() => {
              return recording
                ? streamService.getUpdatedAnalyzerData()
                : new Uint8Array()
            }}
          />
        </div>
      </div>
      <VoiceAuraCanvas
        active={recording}
        stream={stream}
        anchorRef={micButtonRef}
      />
      <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-[2] flex h-[148px] items-end px-24 pb-16">
        <div className="flex w-full px-20">
          <div className="relative z-[3] flex flex-1 items-center">
            {recording ? (
              <RecordTimer />
            ) : (
              <ChatControlButton
                onClick={() => {
                  store.toggleMute()
                }}
              >
                <Icon iconName={state.mute ? 'mute' : 'unmute'} />
              </ChatControlButton>
            )}
          </div>
          <div className="relative z-[3] flex flex-1 justify-center">
            <MictoTooltip />
            <button
              ref={micButtonRef}
              onClick={() => {
                void recordStore.startOrCancelRecording()
              }}
              disabled={state.sendButtonDisabled}
              className={cn(
                'pointer-events-auto size-100 items-center justify-center outline-none',
              )}
            >
              <RecordButtonIcon />
            </button>
          </div>
          <div className="relative z-[3] flex flex-1 items-center justify-end">
            {recording ? (
              <SendButton textInput={false} />
            ) : (
              <ChatControlButton
                onClick={() => {
                  store.setShowTextInput(true)
                }}
              >
                <Icon iconName="keyboard" />
              </ChatControlButton>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function RecordTimer() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const recordStore = useStores().recordStore
  const recordState = useSnapshot(recordStore.state)
  const recoding = state.loading == 'record'
  return recoding ? (
    <motion.div
      className="flex items-center gap-8 text-14 font-semibold text-[#7383AD]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="size-12 animate-pulse rounded-full bg-alert-dark"></div>
      {formatTimer(recordState.recordTimer)}
    </motion.div>
  ) : null
}

interface ProgressBarProps {
  value: number
}

const ProgressBar = (props: ProgressBarProps) => {
  return (
    <div className="my-12 h-8 w-full rounded-full bg-light">
      <div
        className="h-8 rounded-full bg-main text-[#7383AD] transition-width duration-100 ease-linear"
        style={{ width: props.value + '%' }}
      ></div>
    </div>
  )
}

export function VocabularyDrawer() {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  const wordCount = state.vocabulary.length

  const handleClose = () => {
    store.closeVocabularyDrawer()
  }
  const { rive, RiveComponent } = useRive({
    src: '/animations/btn_vocabulary.riv',
    animations: 'Timeline 1',
    autoplay: false,
  })

  useEffect(() => {
    if (!rive?.isPlaying) {
      rive?.play('Timeline 1')
    }
  }, [rive, wordCount])

  return (
    <Drawer open={state.vocabularyDrawerOpen} onClose={handleClose}>
      <DrawerTrigger asChild>
        <div className="relative min-h-[28px] min-w-[80px]">
          <div
            id="vocabulary-drawer-button"
            onClick={() => {
              store.openVocabularyDrawer()
            }}
            className="absolute z-10 flex min-h-[28px] min-w-[80px] items-center justify-center font-nunito-7-condensed text-16 font-extrabold text-white"
          >
            <FormattedMessage id="wordsPlural" values={{ count: wordCount }} />
          </div>
          <RiveComponent className="absolute left-1/2 top-1/2 size-[124px] -translate-x-1/2 translate-y-[calc(-50%-1px)]" />
        </div>
      </DrawerTrigger>
      <DrawerContent
        direction="bottom"
        className="mt-24 flex h-auto flex-col rounded-t bg-white"
      >
        <DrawerBar />
        <div className="flex h-[95svh] flex-col">
          <div className="flex items-center justify-between px-16">
            <div className="ml-12 text-20 font-bold">
              <FormattedMessage id="words" />
            </div>
            <DrawerClose asChild>
              <div
                className="flex size-32 items-center justify-center rounded-full bg-gray"
                onClick={() => {
                  store.setLastAddVocabWord(null)
                  handleClose()
                }}
              >
                <Icon iconName="close" className="text-black" />
              </div>
            </DrawerClose>
          </div>
          <div className="flex-1 overflow-y-auto [scrollbar-width:thin]">
            <VocabularyView />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function Article(props: { title: string; body: string; messageId: number }) {
  const store = useStores().chatStore
  const state = useSnapshot(store.state)
  return (
    <div className="flex flex-col gap-12 rounded-18 bg-gray-light p-16">
      <div className="text-24 font-extrabold">{props.title}</div>
      <div className="text-16 font-semibold">
        <ClickableWords
          disabled={false}
          selectedWords={state.selectedWords}
          onClick={(word, add) => {
            store.handleOpenAddDrawer(word, add, props.messageId)
          }}
        >
          {props.body}
        </ClickableWords>
      </div>
    </div>
  )
}
